import { useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"

type EditableProject = {
  id: string
  name: string
  client: string
  location: string
  startDate: string
  endDate?: string | null
  initialBudget: number
  totalRevenue: number
}

export function EditProjectDialog({
  onOpenChange,
  open,
  project,
}: {
  onOpenChange: (open: boolean) => void
  open: boolean
  project: EditableProject
}) {
  const [form, setForm] = useState({
    client: project.client,
    endDate: project.endDate ?? "",
    initialBudget: String(project.initialBudget),
    location: project.location,
    name: project.name,
    startDate: project.startDate,
    totalRevenue: String(project.totalRevenue),
  })

  function handleSubmit() {
    router.patch(`/projects/${project.id}`, {
      name: form.name,
      client: form.client,
      location: form.location,
      startDate: form.startDate,
      endDate: form.endDate,
      initialBudget: Number(form.initialBudget),
      totalRevenue: Number(form.totalRevenue),
    }, {
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-proj-name">Name</Label>
            <Input id="edit-proj-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Riverside Warehouse" />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-proj-client">Client</Label>
            <Input id="edit-proj-client" value={form.client} onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))} placeholder="Acme Logistics" />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-proj-location">Location</Label>
            <Input id="edit-proj-location" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Portland, OR" />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-proj-start">Start date</Label>
              <Input id="edit-proj-start" type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-proj-end">End date</Label>
              <Input id="edit-proj-end" type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-proj-budget">Initial budget ($)</Label>
              <Input id="edit-proj-budget" type="number" min="0" step="0.01" value={form.initialBudget} onChange={(e) => setForm((f) => ({ ...f, initialBudget: e.target.value }))} placeholder="250000" />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-proj-revenue">Total revenue ($)</Label>
              <Input id="edit-proj-revenue" type="number" min="0" step="0.01" value={form.totalRevenue} onChange={(e) => setForm((f) => ({ ...f, totalRevenue: e.target.value }))} placeholder="300000" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!form.name || !form.client || !form.startDate || !form.endDate || !form.initialBudget || !form.totalRevenue}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
